import { useCallback, useMemo, useRef, useState } from 'react';

import { isWorkflowEndpoint } from 'model/specialActions';
import { cutNodes, pasteNodes as pasteNodesMutation } from 'model/xyflow/mutations';
import type { WorkflowGraph, WorkflowNode } from 'model/xyflowMapping';
import { useWorkflowClipboard } from './useWorkflowClipboard';
import { useWorkflowUndo } from './useWorkflowUndo';

export interface UseWorkflowGraphStateArgs {
  initialGraph: WorkflowGraph;
  initialDirty: boolean;
}

export type GraphUpdater = WorkflowGraph | ((prev: WorkflowGraph) => WorkflowGraph);

/**
 * Delay (ms) after `endDrag` before clearing the `isDraggingRef` flag.
 * xyflow can fire trailing position-sync events right after a drag
 * settles; keeping the flag true briefly stops those trailing syncs
 * from pushing an extra entry onto the undo stack.
 */
const DRAG_FLAG_CLEAR_DELAY_MS = 100;

export interface UseWorkflowGraphStateResult {
  graph: WorkflowGraph;
  isDirty: boolean;
  setIsDirty: (next: boolean) => void;
  /** Replace the graph wholesale (e.g. after Reset/JSON-Save). */
  resetGraph: (next: WorkflowGraph, dirty: boolean) => void;

  /** Apply a mutation function, pushing the pre-image onto the undo stack. */
  apply: (updater: GraphUpdater) => void;

  // Undo / redo
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;

  // Selection
  setSelectedIds: (ids: string[]) => void;

  // Clipboard (selection-aware copy/cut/paste)
  canPaste: boolean;
  copySelected: () => void;
  cutSelected: () => void;
  paste: () => void;

  // Canvas sync (handles drag + post-undo replay + reset suppression).
  // Accepts either a snapshot or a functional updater so rapid bursts
  // (xyflow drag frames) compose against the latest graph rather than
  // racing against React's deferred state.
  applyCanvasChange: (next: GraphUpdater) => void;
  beginDrag: () => void;
  endDrag: () => void;
  setSkipUndoOnce: () => void;
}

/**
 * Owns the editor graph state lifecycle: the current `graph`, the dirty
 * flag, the undo/redo stack, the selection clipboard, and the canvas
 * sync semantics (drag-suppression + post-undo replay + reset
 * suppression).
 *
 * Mutation logic itself lives in `model/xyflow/mutations.ts`. Callers
 * apply mutations via `apply((current) => mutationFn(current, ...))`
 * which automatically pushes onto the undo stack.
 *
 * Three refs handle subtle canvas-sync semantics:
 *   - `skipUndoOnce` flips true right after undo/redo so the very next
 *     xyflow re-sync doesn't push an extra entry onto the stack.
 *   - `suppressDirtyOnce` flips true right before reset/discard so the
 *     subsequent sync from xyflow doesn't immediately mark the graph
 *     dirty again.
 *   - `isDragging` is set during xyflow drag so per-frame position
 *     updates don't pile onto the undo stack.
 */
export const useWorkflowGraphState = ({
  initialGraph,
  initialDirty,
}: UseWorkflowGraphStateArgs): UseWorkflowGraphStateResult => {
  const [graph, setGraph] = useState<WorkflowGraph>(initialGraph);
  const [isDirty, setIsDirty] = useState(initialDirty);

  const selectedIdsRef = useRef<Set<string>>(new Set());
  const skipUndoPushRef = useRef(false);
  const suppressDirtyRef = useRef(false);
  const isDraggingRef = useRef(false);

  const undoStack = useWorkflowUndo<WorkflowGraph>();
  const clipboard = useWorkflowClipboard();

  const apply = useCallback(
    (updater: GraphUpdater) => {
      setGraph((prev) => {
        undoStack.push(prev);
        return typeof updater === 'function' ? updater(prev) : updater;
      });
      setIsDirty(true);
    },
    [undoStack],
  );

  // --- Selection ---
  const setSelectedIds = useCallback((ids: string[]) => {
    selectedIdsRef.current = new Set(ids);
  }, []);

  const getSelectedNodes = useCallback((current: WorkflowGraph): WorkflowNode[] => {
    const ids = selectedIdsRef.current;
    if (ids.size > 0) return current.nodes.filter((n) => ids.has(n.id));
    return current.nodes.filter((n) => n.selected);
  }, []);

  // --- Undo / redo ---
  const undo = useCallback(() => {
    setGraph((current) => {
      const prev = undoStack.undo(current);
      if (prev === null) return current;
      skipUndoPushRef.current = true;
      setIsDirty(true);
      return prev;
    });
  }, [undoStack]);

  const redo = useCallback(() => {
    setGraph((current) => {
      const next = undoStack.redo(current);
      if (next === null) return current;
      skipUndoPushRef.current = true;
      setIsDirty(true);
      return next;
    });
  }, [undoStack]);

  // --- Clipboard ---
  const copySelected = useCallback(() => {
    setGraph((current) => {
      const selected = getSelectedNodes(current);
      if (selected.length > 0) clipboard.copy({ nodes: selected, edges: [] });
      return current;
    });
  }, [clipboard, getSelectedNodes]);

  const cutSelected = useCallback(() => {
    setGraph((current) => {
      const selected = getSelectedNodes(current);
      const cuttable = selected.filter((n) => !isWorkflowEndpoint(n.data.actionId));
      if (cuttable.length === 0) return current;
      clipboard.cut({ nodes: cuttable, edges: [] });
      undoStack.push(current);
      return cutNodes(current, new Set(cuttable.map((n) => n.id)));
    });
    setIsDirty(true);
  }, [clipboard, undoStack, getSelectedNodes]);

  const paste = useCallback(() => {
    const toPaste = clipboard.paste();
    if (!toPaste || toPaste.nodes.length === 0) return;
    apply((current) => pasteNodesMutation(current, toPaste.nodes));
  }, [apply, clipboard]);

  // --- Canvas sync ---
  const applyCanvasChange = useCallback(
    (next: GraphUpdater) => {
      const resolve = (prev: WorkflowGraph): WorkflowGraph =>
        typeof next === 'function' ? next(prev) : next;

      if (suppressDirtyRef.current) {
        suppressDirtyRef.current = false;
        setGraph(resolve);
        return;
      }

      if (skipUndoPushRef.current) {
        skipUndoPushRef.current = false;
        setGraph(resolve);
        setIsDirty(true);
        return;
      }

      if (isDraggingRef.current) {
        setGraph(resolve);
        setIsDirty(true);
        return;
      }
      setGraph((prev) => {
        undoStack.push(prev);
        return resolve(prev);
      });
      setIsDirty(true);
    },
    [undoStack],
  );

  const beginDrag = useCallback(() => {
    isDraggingRef.current = true;
    setGraph((prev) => {
      undoStack.push(prev);
      return prev;
    });
  }, [undoStack]);

  const endDrag = useCallback(() => {
    // Keep the flag true briefly so trailing post-drag syncs from xyflow
    // don't pile an extra entry onto the undo stack.
    setTimeout(() => {
      isDraggingRef.current = false;
    }, DRAG_FLAG_CLEAR_DELAY_MS);
  }, []);

  const resetGraph = useCallback(
    (next: WorkflowGraph, dirty: boolean) => {
      suppressDirtyRef.current = true;
      setGraph(next);
      setIsDirty(dirty);
      undoStack.clear();
    },
    [undoStack],
  );

  const setSkipUndoOnce = useCallback(() => {
    skipUndoPushRef.current = true;
  }, []);

  return useMemo(
    () => ({
      graph,
      isDirty,
      setIsDirty,
      resetGraph,
      apply,
      canUndo: undoStack.canUndo,
      canRedo: undoStack.canRedo,
      undo,
      redo,
      setSelectedIds,
      canPaste: clipboard.canPaste,
      copySelected,
      cutSelected,
      paste,
      applyCanvasChange,
      beginDrag,
      endDrag,
      setSkipUndoOnce,
    }),
    [
      graph,
      isDirty,
      resetGraph,
      apply,
      undoStack.canUndo,
      undoStack.canRedo,
      undo,
      redo,
      setSelectedIds,
      clipboard.canPaste,
      copySelected,
      cutSelected,
      paste,
      applyCanvasChange,
      beginDrag,
      endDrag,
      setSkipUndoOnce,
    ],
  );
};
